/**
 * ConfirmDialog — shared themed replacement for window.confirm()/alert() (#5274).
 *
 * Modeled on UnsavedChangesGuardModal: a stateless presentational leaf whose
 * root carries a `*-overlay` class + role="dialog" so useModulePaneEscapeClose's
 * DOM guard treats it as a modal (escape-layer contract, apps/web/AGENTS.md).
 * Escape and backdrop click cancel via useEscapeKey (claims the keypress).
 *
 * Most call sites use the promise-based helpers instead of the component:
 *   if (!(await confirmDialog({ message: 'Delete?', danger: true }))) return;
 *   await alertDialog({ message: 'Something you must acknowledge.' });
 * The helpers mount a throwaway React root on document.body, so they work from
 * any handler without a provider. Fire-and-forget notifications should use
 * useToast() instead — reserve alertDialog for genuinely blocking messages.
 */
import { useEffect, useId, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useDialogFocus } from '@/common/hooks/useDialogFocus';

export interface ConfirmDialogProps {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive action: confirm button renders in the danger color. */
  danger?: boolean;
  /** Alert mode: single acknowledge button; Escape/backdrop also confirm. */
  alertOnly?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger,
  alertOnly,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dismiss = alertOnly ? onConfirm : onCancel;
  const cardRef = useRef<HTMLDivElement>(null);
  // Name the dialog for screen readers: autoFocus lands on a button, so without a
  // label an SR user hears only "Cancel, button" with no idea WHAT they're
  // confirming (26 call sites, incl. danger deletes). Point aria-labelledby at the
  // title when present (fall back to a generic label), and describe with the message.
  const titleId = useId();
  const messageId = useId();

  // Focus trap + restore (#5263, F-a11y-07): this overlay is a `role="dialog"
  // aria-modal` surface, often mounted imperatively (mountDialog below) on top
  // of whatever page/modal had focus — without a trap, Tab walks straight out
  // into the page behind it, and closing never gave focus back to the
  // trigger. Coexists with the `autoFocus` below (the hook no-ops its own
  // initial-focus move when autoFocus already landed inside the card).
  useDialogFocus(cardRef);

  // Hand-rolled Escape (escape-layer contract, apps/web/AGENTS.md): the dialog
  // is imperatively stacked ABOVE whatever modal opened it, but that host's
  // useEscapeKey listener registered first and would win a bubble-phase race.
  // Claiming the keypress in the CAPTURE phase keeps one Esc = this layer only;
  // preventDefault() makes every downstream consumer decline.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [dismiss]);

  return (
    <div
      className="confirm-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : alertOnly ? 'Alert' : 'Confirm'}
      aria-describedby={messageId}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2147483647, padding: 16,
      }}
      onClick={dismiss}
    >
      <div
        ref={cardRef}
        style={{
          background: 'var(--color-surface)', borderRadius: 16, padding: 24, maxWidth: 360, width: '100%',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)', textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <p id={titleId} style={{ fontSize: 15, fontWeight: 500, color: 'var(--color-text)', margin: '0 0 6px' }}>{title}</p>
        )}
        <p id={messageId} style={{ fontSize: 13, color: 'var(--color-text-muted, #4a4d51)', margin: '0 0 20px', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {!alertOnly && (
            <button
              className="confirm-dialog-cancel-btn"
              autoFocus={danger}
              onClick={onCancel}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #DADCE0', background: 'var(--color-surface)', fontSize: 13, cursor: 'pointer', color: 'var(--color-text-muted, #4a4d51)' }}
            >
              {cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            className={`confirm-dialog-confirm-btn${danger ? ' confirm-dialog-confirm-btn--danger' : ''}`}
            autoFocus={alertOnly || !danger}
            onClick={onConfirm}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: danger ? 'var(--color-danger, #ea4335)' : '#1a1a1a',
              color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500,
            }}
          >
            {confirmLabel ?? (alertOnly ? 'OK' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

function mountDialog(props: Omit<ConfirmDialogProps, 'onConfirm' | 'onCancel'>, resolve: (ok: boolean) => void): void {
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  const settle = (ok: boolean) => {
    // Defer unmount out of the React event that triggered it.
    setTimeout(() => {
      root.unmount();
      host.remove();
    }, 0);
    resolve(ok);
  };
  root.render(<ConfirmDialog {...props} onConfirm={() => settle(true)} onCancel={() => settle(false)} />);
}

/** Promise-based window.confirm() replacement. Escape/backdrop resolve false. */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => mountDialog(options, resolve));
}

/** Promise-based window.alert() replacement for blocking messages. */
export function alertDialog(options: { title?: string; message: string; confirmLabel?: string }): Promise<void> {
  return new Promise((resolve) => mountDialog({ ...options, alertOnly: true }, () => resolve()));
}
